import React from 'react';

/**
 * Process inline formatting like bold, code, links, confidence badges
 */
export const processInlineFormatting = (text) => {
  if (typeof text !== 'string') return text;

  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Handle confidence badges
    const confidenceMatch = remaining.match(/^(.*?)\[Confidence: (High|Medium|Low) \(([\d\.]+)\)\](.*)/);
    if (confidenceMatch) {
      if (confidenceMatch[1]) {
        parts.push(<span key={key++}>{confidenceMatch[1]}</span>);
      }
      const level = confidenceMatch[2];
      const value = confidenceMatch[3];
      parts.push(
        <span key={key++} className={`confidence-badge confidence-${level.toLowerCase()}`}>
          {level} ({value})
        </span>
      );
      remaining = confidenceMatch[4];
      continue;
    }

    // Handle DOI links
    const doiMatch = remaining.match(/^(.*?)(https:\/\/doi\.org\/[\w\.\-\/]+)(.*)/);
    if (doiMatch) {
      if (doiMatch[1]) parts.push(<span key={key++}>{doiMatch[1]}</span>);
      parts.push(
        <a
          key={key++}
          href={doiMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="doi-link"
        >
          {doiMatch[2]}
        </a>
      );
      remaining = doiMatch[3];
      continue;
    }

    // Handle research URLs
    const urlMatch = remaining.match(/^(.*?)(https?:\/\/(?:ieeexplore\.ieee\.org|pubmed\.ncbi\.nlm\.nih\.gov|scholar\.google\.com|nature\.com|sciencedirect\.com|acm\.org|researchgate\.net|arxiv\.org)[^\s\)]*)(.*)/);
    if (urlMatch) {
      if (urlMatch[1]) parts.push(<span key={key++}>{urlMatch[1]}</span>);
      parts.push(
        <a
          key={key++}
          href={urlMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="content-link"
        >
          {urlMatch[2]}
        </a>
      );
      remaining = urlMatch[3];
      continue;
    }

    // Handle bold text
    const boldMatch = remaining.match(/^(.*?)\*\*([^*]+?)\*\*(.*)/);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
      parts.push(<strong key={key++}>{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // Handle inline code
    const codeMatch = remaining.match(/^(.*?)`(.*?)`(.*)/);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
      parts.push(
        <code key={key++} className="inline-code">
          {codeMatch[2]}
        </code>
      );
      remaining = codeMatch[3];
      continue;
    }

    // No more special formatting, add remaining text
    let cleanText = remaining.replace(/\*\*/g, '');
    parts.push(<span key={key++}>{cleanText}</span>);
    break;
  }

  return parts.length > 1 ? <>{parts}</> : parts[0] || text;
};

/**
 * Parse HTML content with tables and other elements
 */
const parseHTMLContent = (htmlString) => {
  const elements = [];
  let key = 0;
  
  // Split content by HTML table tags
  const parts = htmlString.split(/(<table>[\s\S]*?<\/table>)/gi);
  
  parts.forEach((part, index) => {
    if (!part.trim()) return;
    
    // Check if this part is an HTML table
    if (part.trim().toLowerCase().startsWith('<table')) {
      // Parse the HTML table
      const tableElement = parseHTMLTable(part, `table-${key++}`);
      if (tableElement) {
        elements.push(tableElement);
      }
    } else {
      // Process as regular markdown content
      const lines = part.split('\n');
      lines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) {
          elements.push(<div key={`space-${key++}`} className="content-spacer"></div>);
          return;
        }
        
        // Handle headers
        if (trimmedLine.match(/^[🛠️📈🎯📋💰⚠️🧪✅🔬🔍👥🏭📄📚🔗📝]/)) {
          elements.push(
            <h3 key={`header-${key++}`} className="content-main-header">
              {trimmedLine}
            </h3>
          );
        }
        // Handle sub-headers
        else if (
          trimmedLine.startsWith('###') ||
          trimmedLine.startsWith('##') ||
          trimmedLine.match(/^\*\*.*\*\*:?$/)
        ) {
          let headerText = trimmedLine
            .replace(/^###?\s*/, '')
            .replace(/^\*\*/, '')
            .replace(/\*\*:?$/, '');
          elements.push(
            <h4 key={`subheader-${key++}`} className="content-sub-header">
              {headerText}
            </h4>
          );
        }
        // Handle bullet points
        else if (trimmedLine.match(/^[•\-\*\+]\s/) || line.match(/^\s+[•\-\*\+]\s/)) {
          const content = trimmedLine.replace(/^[•\-\*\+]\s/, '');
          const indentLevel = Math.floor((line.length - line.trimStart().length) / 2);
          const isSubItem = indentLevel > 0 || line.startsWith('  ') || line.startsWith('\t');
          
          elements.push(
            <div
              key={`bullet-${key++}`}
              className={`bullet-item ${isSubItem ? 'sub-bullet' : 'main-bullet'}`}
              style={{ marginLeft: `${indentLevel * 20}px` }}
            >
              <span className="bullet-point">•</span>
              <span className="bullet-content">{processInlineFormatting(content)}</span>
            </div>
          );
        }
        // Handle numbered lists
        else if (trimmedLine.match(/^\d+\.\s/)) {
          const content = trimmedLine.replace(/^\d+\.\s/, '');
          const number = trimmedLine.match(/^(\d+)\./)[1];
          
          elements.push(
            <div key={`numbered-${key++}`} className="numbered-item">
              <span className="number-point">{number}.</span>
              <span className="numbered-content">{processInlineFormatting(content)}</span>
            </div>
          );
        }
        // Regular paragraphs
        else if (trimmedLine.length > 0) {
          elements.push(
            <p key={`para-${key++}`} className="content-paragraph">
              {processInlineFormatting(trimmedLine)}
            </p>
          );
        }
      });
    }
  });
  
  return elements;
};

/**
 * Parse an HTML table string into a React table element
 */
const parseHTMLTable = (htmlTableString, key) => {
  try {
    // Extract table content
    const theadMatch = htmlTableString.match(/<thead>([\s\S]*?)<\/thead>/i);
    const tbodyMatch = htmlTableString.match(/<tbody>([\s\S]*?)<\/tbody>/i);
    
    if (!theadMatch && !tbodyMatch) return null;
    
    // Parse header rows
    let headers = [];
    if (theadMatch) {
      const headerRowsMatch = theadMatch[1].match(/<tr>([\s\S]*?)<\/tr>/gi);
      if (headerRowsMatch) {
        const thMatches = headerRowsMatch[0].match(/<th[^>]*>([\s\S]*?)<\/th>/gi);
        if (thMatches) {
          headers = thMatches.map(th => 
            th.replace(/<\/?th[^>]*>/gi, '').trim()
          );
        }
      }
    }
    
    // Parse body rows
    let rows = [];
    if (tbodyMatch) {
      const bodyRowsMatch = tbodyMatch[1].match(/<tr>([\s\S]*?)<\/tr>/gi);
      if (bodyRowsMatch) {
        rows = bodyRowsMatch.map(row => {
          const tdMatches = row.match(/<td[^>]*>([\s\S]*?)<\/td>/gi);
          if (tdMatches) {
            return tdMatches.map(td => 
              td.replace(/<\/?td[^>]*>/gi, '').trim()
            );
          }
          return [];
        });
      }
    }
    
    // Render the table
    return (
      <div key={key} className="table-wrapper html-table-wrapper">
        <table className="content-table html-content-table">
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((header, i) => (
                  <th key={i}>{processInlineFormatting(header)}</th>
                ))}
              </tr>
            </thead>
          )}
          {rows.length > 0 && (
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{processInlineFormatting(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    );
  } catch (error) {
    console.error('Error parsing HTML table:', error);
    return null;
  }
};

/**
 * Parse and format content into React elements
 */
export const formatContent = (content) => {
  if (!content) return <p className="no-content">No content available</p>;
  
  const contentStr = typeof content === 'string' ? content : String(content);
  
  // Check if content contains HTML tables
  if (contentStr.includes('<table>') || contentStr.includes('<table ')) {
    return parseHTMLContent(contentStr);
  }
  
  const lines = contentStr.split('\n');
  const elements = [];
  let seenHeaders = new Set();
  let tableHeaders = [];
  let tableRows = [];
  let isInTable = false;

  const closeTable = () => {
    if (isInTable && tableHeaders.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="table-wrapper">
          <table className="content-table">
            <thead>
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i}>{processInlineFormatting(header)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{processInlineFormatting(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      isInTable = false;
      tableHeaders = [];
      tableRows = [];
    }
  };

  const processLine = (line, index) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      closeTable();
      elements.push(<div key={`space-${index}`} className="content-spacer"></div>);
      return;
    }

    if (trimmedLine.match(/^[=\-]+$/)) {
      return;
    }

    const headerKey = trimmedLine
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '_');

    if (seenHeaders.has(headerKey)) {
      return;
    }

    // Handle main headers (with emojis)
    if (trimmedLine.match(/^[🛠️📈🎯📋💰⚠️🧪✅🔬🔍👥🏭📄📚🔗📝]/)) {
      closeTable();
      seenHeaders.add(headerKey);
      elements.push(
        <h3 key={index} className="content-main-header">
          {trimmedLine}
        </h3>
      );
    }
    // Handle sub-headers
    else if (
      trimmedLine.startsWith('###') ||
      trimmedLine.startsWith('##') ||
      trimmedLine.match(/^\*\*.*\*\*:?$/)
    ) {
      closeTable();
      let headerText = trimmedLine
        .replace(/^###?\s*/, '')
        .replace(/^\*\*/, '')
        .replace(/\*\*:?$/, '');
      elements.push(
        <h4 key={index} className="content-sub-header">
          {headerText}
        </h4>
      );
    }
    // Handle table rows
    else if (trimmedLine.includes('|') && trimmedLine.split('|').length > 2) {
      const cells = trimmedLine
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell && cell !== '---');

      if (cells.length > 1) {
        if (!isInTable) {
          isInTable = true;
          tableHeaders = cells;
        } else {
          if (!cells.every((cell) => cell.match(/^[\-\s]*$/))) {
            tableRows.push(cells);
          }
        }
      }
    }
    // Handle bullet points
    else if (
      trimmedLine.match(/^[•\-\*\+]\s/) ||
      line.match(/^\s+[•\-\*\+]\s/)
    ) {
      closeTable();
      const content = trimmedLine.replace(/^[•\-\*\+]\s/, '');
      const indentLevel = Math.floor((line.length - line.trimStart().length) / 2);
      const isSubItem = indentLevel > 0 || line.startsWith('  ') || line.startsWith('\t');

      elements.push(
        <div
          key={index}
          className={`bullet-item ${isSubItem ? 'sub-bullet' : 'main-bullet'}`}
          style={{ marginLeft: `${indentLevel * 20}px` }}
        >
          <span className="bullet-point">•</span>
          <span className="bullet-content">{processInlineFormatting(content)}</span>
        </div>
      );
    }
    // Handle numbered lists
    else if (trimmedLine.match(/^\d+\.\s/)) {
      closeTable();
      const content = trimmedLine.replace(/^\d+\.\s/, '');
      const number = trimmedLine.match(/^(\d+)\./)[1];

      elements.push(
        <div key={index} className="numbered-item">
          <span className="number-point">{number}.</span>
          <span className="numbered-content">{processInlineFormatting(content)}</span>
        </div>
      );
    }
    // Regular paragraphs
    else {
      closeTable();
      elements.push(
        <p key={index} className="content-paragraph">
          {processInlineFormatting(trimmedLine)}
        </p>
      );
    }
  };

  lines.forEach((line, index) => {
    processLine(line, index);
  });

  closeTable();
  return elements;
};
